import { z } from 'zod';
import { type Metric, metricsOf, type ResultRow, rowsOf } from './resultRows.js';
import { list, num, records, text } from './resultValues.js';

const nonBlank = (field: string) =>
  z.string().refine((value) => value.trim().length > 0, `${field} must not be blank`);

export const combinationListSchema = z.object({
  combinations: z.array(
    z.looseObject({
      combo_id: nonBlank('Combination id'),
      elements: z.array(
        z.looseObject({ category: z.string(), feature: nonBlank('Feature'), tag: z.string() }),
      ),
    }),
  ),
  // The results header, worded by the service.
  count_label: z.string().nullish(),
});

export type CombinationList = z.infer<typeof combinationListSchema>;

export interface ComboElement {
  category: string;
  scene: string | null;
  feature: string;
  tag: string;
  correlated: boolean;
}

export interface ElementTagRow {
  key: string;
  tag: string;
  title: string;
  scenes: string[];
}

export interface CategoryRow {
  category: string;
  tags: ElementTagRow[];
}

export interface ComboVariant {
  key: string;
  elements: ComboElement[];
  support: number;
  uplift: number | null;
}

export interface Combo {
  key: string;
  network: string | null;
  elements: ComboElement[];
  size: number;
  spendShare: number | null;
  metrics: Metric[];
  // Why a correlated element is marked; without it the marker is hidden.
  correlatedNote: string | null;
  // The lift checks the set passed, labelled by the service.
  checks: Metric[];
  // The card's badge, worded by the service.
  verdict: string | null;
  // Heads the variants; without it they are not listed.
  variantsLabel: string | null;
  variants: ComboVariant[];
  // Rolled up under this one but not listed in `variants`.
  hiddenVariants: number;
  // The service's count when it lists ids rather than creative rows (Winning Combinations).
  support: number;
  creatives: ResultRow[];
}

function elementOf(record: Record<string, unknown>, correlated: boolean): ComboElement {
  return {
    category: text(record['category']) ?? '',
    scene: text(record['scene']),
    feature: text(record['feature']) ?? '',
    tag: text(record['tag']) ?? '',
    correlated,
  };
}

function correlatedPositions(value: unknown): Set<number> {
  return new Set(list(value).flatMap((pair) => list(pair).flatMap((index) => num(index) ?? [])));
}

// What a chip reads: a tag alone repeats across features ("Included" for a logo,
// a CTA and subtitles), and a feature across scenes, so both name it.
export function chipLabelOf(element: ComboElement): string {
  const named = element.feature === '' ? element.tag : `${element.feature}: ${element.tag}`;
  return element.scene === null ? named : `${element.scene} · ${named}`;
}

export function elementLabelOf(element: ComboElement): string {
  return [element.category, element.scene ?? '', element.feature, element.tag]
    .filter((part) => part !== '')
    .join(' › ');
}

// One row per category, one tag per feature + tag: an element seen in two
// scenes is one tag with two dots.
export function categoryRowsOf(elements: ComboElement[]): CategoryRow[] {
  const rows = new Map<string, Map<string, ElementTagRow>>();
  for (const element of elements) {
    const tags = rows.get(element.category) ?? new Map<string, ElementTagRow>();
    rows.set(element.category, tags);
    const key = `${element.feature}|${element.tag}`;
    const tag = tags.get(key) ?? {
      key,
      tag: element.tag,
      title: element.feature === '' ? element.tag : `${element.feature}: ${element.tag}`,
      scenes: [],
    };
    tags.set(key, tag);
    if (element.scene !== null && !tag.scenes.includes(element.scene))
      tag.scenes.push(element.scene);
  }
  return [...rows].map(([category, tags]) => ({ category, tags: [...tags.values()] }));
}

export function elementCountsOf(combos: Combo[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const element of combos.flatMap((combo) => combo.elements)) {
    const label = elementLabelOf(element);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return counts;
}

// Leads with the element the fewest cards share: elements arrive sorted by
// category, so in a run anchored on one gem every card would start alike.
export function comboTitleOf(combo: Combo, elementCounts = new Map<string, number>()): string {
  const countOf = (element: ComboElement) => elementCounts.get(elementLabelOf(element)) ?? 0;
  const [lead, ...rest] = combo.elements.toSorted((a, b) => countOf(a) - countOf(b));
  if (lead === undefined) return '';
  return rest.length === 0 ? chipLabelOf(lead) : `${chipLabelOf(lead)} + ${rest.length} more`;
}

export function comboOf(record: CombinationList['combinations'][number]): Combo {
  const correlated = correlatedPositions(record['correlated']);
  const variants = records(record['variants']).map((variant, index) => ({
    key: String(index),
    elements: records(variant['elements']).map((element) => elementOf(element, false)),
    support: num(variant['support']) ?? 0,
    uplift: num(variant['uplift']),
  }));
  const creatives = rowsOf({ creatives: record['creatives'] }, 'creative_list');
  return {
    key: record.combo_id,
    network: text(record['network']),
    elements: record.elements.map((element, index) => elementOf(element, correlated.has(index))),
    size: num(record['size']) ?? record.elements.length,
    spendShare: num(record['spend_share']),
    metrics: metricsOf(record),
    checks: metricsOf({ metrics: record['checks'] }),
    correlatedNote: text(record['correlated_note']),
    verdict: text(record['verdict']),
    variantsLabel: text(record['variants_label']),
    variants,
    hiddenVariants: Math.max((num(record['variant_count']) ?? 0) - variants.length, 0),
    support: num(record['creative_count']) ?? creatives.length,
    creatives,
  };
}

export function combosOf(result: CombinationList): Combo[] {
  return result.combinations.map(comboOf);
}

export function comboKeysOf(combos: Combo[]): string[] {
  return combos.map((combo) => combo.key);
}
